import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnDestroy,
  inject,
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { PRIVATE_MENU_ICONS } from './menu.icons';

import { AuthService } from '@/app/features/auth/services/auth.service';

import {
  PRIVATE_DESTINATION_COUNTRIES,
  type DestinationCountryKey,
} from './menu.destination-countries';

import { PRIVATE_LANGUAGE_OPTIONS } from './menu.languages';
import { PRIVATE_CURRENCY_OPTIONS } from './menu.currencies';

import type { CurrencyKey } from '@/app/core/currency/interfaces/currency.types';
import type { LanguageKey } from '@/app/core/language/interfaces/language.types';

import { CurrencyFacade } from '@/app/core/currency/facades/currency.facade';
import { CountryFacade } from '@/app/core/country/facades/country.facade';
import { LanguageFacade } from '@/app/core/language/facades/language.facade';
import { PublicConfigService } from '@/app/core/config/public-config.service';
import { BodyScrollLockService } from '@/app/core/services/body-scroll-lock.service';
import { ToastService } from '@/app/core/services/toast/toast.service';

export type PrivateNavbarMenuAction =
  | 'customer_service'
  | 'how_to_quote'
  | 'how_to_ship'
  | 'my_account'
  | 'logout'
  | 'login'
  | 'locker_address'
  | 'currency'
  | 'language'
  | 'destination_country';

@Component({
  selector: 'app-menu',
  standalone: true,
  imports: [CommonModule, RouterLink, TranslateModule],
  templateUrl: './menu.html',
  styleUrl: './menu.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrivateNavbarMenuComponent implements OnInit, OnDestroy {
  @Input() topOffsetPx = 0;
  @Input() bottomOffsetPx = 0;

  @Output() closed = new EventEmitter<void>();
  @Output() action = new EventEmitter<PrivateNavbarMenuAction>();

  readonly icons = PRIVATE_MENU_ICONS;

  private readonly doc = inject(DOCUMENT);
  private readonly authService = inject(AuthService);
  private readonly publicConfig = inject(PublicConfigService);
  private readonly scrollLock = inject(BodyScrollLockService);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);

  readonly currencyFacade = inject(CurrencyFacade);
  readonly countryFacade = inject(CountryFacade);
  readonly languageFacade = inject(LanguageFacade);

  readonly currencyKey = this.currencyFacade.currencyKey;
  readonly isAuthenticated = this.authService.authenticated;

  // ── Nombre del usuario desde localStorage
  get userName(): string {
    return this.authService.user?.name?.trim() || 'Usuario';
  }

  // ── Nombre del envío: "Nombre Apellido l LockerId"
  get shippingName(): string {
    const user = this.authService.user;
    if (!user) return '';
    const parts = [user.name?.trim(), user.lastName?.trim()].filter(Boolean).join(' ');
    const lockerId = user.locker?.id;
    return lockerId ? `${parts} l ${lockerId}` : parts;
  }

  //  UI STATES
  helpExpanded = true;

  // --- Locker ---
  lockerExpanded = false;
  lockerHelpOpen = false;

  // --- Quote help ---
  quoteHelpOpen = false;
  readonly quoteHelpVideoSrc = 'home/navbar/menu/como-cotizar.mp4';

  /** Dirección del casillero desde config_data (Datos de Contacto › Dirección). */
  readonly lockerAddress = this.publicConfig.lockerAddress;

  readonly lockerMapImages = {
    desktop: 'home/navbar/menu/warehouseaddress.svg',
    tablet: 'home/navbar/menu/warehouseaddresstablet.svg',
    mobile: 'home/navbar/menu/warehouseaddressmobile.svg',
  };

  // --- Destination ---
  destinationExpanded = false;
  readonly destinationCountries = PRIVATE_DESTINATION_COUNTRIES;

  get destinationCountryKey(): DestinationCountryKey {
    return this.countryFacade.countryKey();
  }

  get destinationCountryLabel(): string {
    return (
      this.destinationCountries.find((c) => c.key === this.destinationCountryKey)?.label ??
      'Colombia'
    );
  }

  // --- Language ---
  languageExpanded = false;
  readonly languageOptions = PRIVATE_LANGUAGE_OPTIONS;

  get languageKey(): LanguageKey {
    return this.languageFacade.languageKey();
  }

  get languageLabel(): string {
    return this.languageOptions.find((l) => l.key === this.languageKey)?.label ?? 'MENU.LANGUAGE.OPTION_ES';
  }

  // --- Currency ---
  currencyExpanded = false;
  /** Panamá opera solo en USD (sus órdenes y envíos quedan con TRM 1), así que no se ofrece COP. */
  get currencyOptions(): ReadonlyArray<(typeof PRIVATE_CURRENCY_OPTIONS)[number]> {
    return this.destinationCountryKey === 'panama'
      ? PRIVATE_CURRENCY_OPTIONS.filter((c) => c.key === 'usd')
      : PRIVATE_CURRENCY_OPTIONS;
  }

  get currencyLabel(): string {
    const key = this.currencyKey();
    return this.currencyOptions.find((c) => c.key === key)?.label ?? 'MENU.CURRENCY.OPTION_USD';
  }

  //  LIFECYCLE
  ngOnInit(): void {
    this.publicConfig.load();
    this.scrollLock.lock();
  }

  ngOnDestroy(): void {
    this.scrollLock.unlock();
  }

  //  ACTIONS
  close(): void {
    this.lockerHelpOpen = false;
    this.closed.emit();
  }

  get customerServiceUrl(): string {
    return this.publicConfig.customerServiceUrl();
  }

  emit(action: PrivateNavbarMenuAction): void {
    this.action.emit(action);
  }

  // --- Locker ---
  toggleLocker(): void { this.lockerExpanded = !this.lockerExpanded; }
  openLockerHelp(): void { this.lockerHelpOpen = true; this.quoteHelpOpen = false; }
  closeLockerHelp(): void { this.lockerHelpOpen = false; }

  // --- Quote help ---
  openQuoteHelp(): void { this.quoteHelpOpen = true; this.lockerHelpOpen = false; }
  closeQuoteHelp(): void { this.quoteHelpOpen = false; }

  async copyToClipboard(text: string, copiedMessageKey = 'MENU.LOCKER.COPIED'): Promise<void> {
    const value = (text ?? '').trim();
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const ta = this.doc.createElement('textarea');
      ta.value = value;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      this.doc.body.appendChild(ta);
      ta.select();
      this.doc.execCommand('copy');
      this.doc.body.removeChild(ta);
    }

    this.toastService.show(this.translate.instant(copiedMessageKey), 'success');
  }

  // --- Help ---
  toggleHelp(): void { this.helpExpanded = !this.helpExpanded; }

  // --- Destination ---
  toggleDestination(): void { this.destinationExpanded = !this.destinationExpanded; }
  setDestinationCountry(key: DestinationCountryKey): void {
    this.countryFacade.setCountry(key);

    if (key === 'panama') {
      this.currencyFacade.setCurrency('usd');
    } else if (key === 'colombia') {
      this.currencyFacade.setCurrency('cop');
    }

    this.close();
  }

  // --- Language ---
  toggleLanguage(): void { this.languageExpanded = !this.languageExpanded; }
  setLanguage(key: LanguageKey): void {
    this.languageFacade.setLanguage(key);
    this.close();
  }

  // --- Currency ---
  toggleCurrency(): void { this.currencyExpanded = !this.currencyExpanded; }
  setCurrency(key: CurrencyKey): void {
    this.currencyFacade.setCurrency(key);
    this.close();
  }
}
